import { PlentyGoalAccountFinancialEntity } from '@common/financialEntities';
import { calculateForecastAmount } from '@common/forecasting';
import { Goal } from '@common/goals';
import { FinancialEntitySnapshotFieldsFragment } from '@common/graphql/generated';
import {
    getCappedBalance,
    getGoalAccountBalance,
    GoalSnapshot,
} from '@common/react/goals';
import styleguide from '@common/styleguide';
import { TransactionSchedulePlan } from '@common/types';
import { dt, getEarliestDate } from '@common/util/dateTime';
import { isNumber } from '@common/util/types';
import {
    endOfTickDateStr,
    findValueNode,
    GoalValueNode,
    makeTick,
    makeValueSeriesGenerator,
    Tick,
    TickRange,
} from '@common/valueSeries';
import { configByGoalType } from './config';
import { GoalsChartEntityParams } from './GoalsChart';
import { getGoalForecastStartAmount } from './helpers';

export const getSingleplayerOnboardingPlanChartEntities = ({
    goal,
    targetDate,
    annualReturn,
    initialDepositPlan,
    transactionSchedulePlan,
    forecastCompletionDate,
    range,
}: {
    goal: Goal;
    /* fixed date for targeted goals, floating date for untargeted goals */
    targetDate: string | null | undefined;
    annualReturn: number;
    initialDepositPlan: number;
    transactionSchedulePlan: TransactionSchedulePlan;
    forecastCompletionDate?: string;
    range: TickRange;
}): GoalsChartEntityParams[] => {
    const currentDate = dt().toDateStr();
    const targetAmount = goal.targetAmount ?? 0;
    const goalId = goal.id;
    const type = goal.type;
    const name = 'Total';
    const color = configByGoalType[goal.type].color;

    const getForecastValue = (tick: Tick) => {
        const currentValue = initialDepositPlan;

        let balance: number | null = calculateForecastAmount({
            startAmount: currentValue,
            annualGrowthRate: annualReturn,
            transactionSchedulePlans: [transactionSchedulePlan],
            startDate: currentDate,
            targetDate: getEarliestDate(
                endOfTickDateStr(tick),
                forecastCompletionDate
            ),
        });

        // Cap at forecast completion date
        if (targetAmount && targetDate) {
            balance = getCappedBalance({
                balance,
                currentValue,
                targetAmount,
            });
        }

        return balance;
    };

    const vsg = makeValueSeriesGenerator<GoalSnapshot, GoalValueNode>({
        snapshots: [],
        currentDate,
        generatePastValueNode: ({ tick }) => {
            return { ...tick, balance: 0, goalId, type, name, color };
        },
        generateCurrentValueNode: ({ tick }) => {
            const balance = getForecastValue(tick);
            return { ...tick, balance, goalId, type, name, color };
        },
        generateFutureValueNode: ({ tick }) => {
            const balance = getForecastValue(tick);
            return { ...tick, balance, goalId, type, name, color };
        },
    });

    // N.B. There's no point in memoizing the vsg separately, here,
    // because range will only change as a result of the vsg inputs changing.
    const valueSeries = vsg(range);

    const config = configByGoalType[goal.type];

    let callout;
    if (targetDate) {
        const targetTick = makeTick(targetDate, range.unit);
        const targetAmount =
            goal.targetAmount ?? findValueNode(valueSeries, targetTick).balance;
        if (isNumber(targetAmount)) {
            callout = {
                iconName: config.iconName,
                color: config.color,
                date: makeTick(targetDate, range.unit).date,
                value: targetAmount,
            };
        }
    }

    const entity: GoalsChartEntityParams = {
        id: goal.id,
        valueSeries,
        color: config.color,
        callout,
    };

    return [entity];
};

export const getMultiplayerOnboardingPlanChartEntities = ({
    goal,
    targetDate,
    annualReturn,
    initialDepositPlan,
    transactionSchedulePlan,
    forecastCompletionDate,
    range,
    partnerGoalAccount,
    partnerGoalAccountSnapshots,
    partnerInitialDepositPlan,
    partnerTransactionSchedulePlan,
    partnerAnnualReturn,
}: {
    goal: Goal;
    targetDate: string | null | undefined;
    annualReturn: number;
    initialDepositPlan: number;
    transactionSchedulePlan: TransactionSchedulePlan;
    forecastCompletionDate?: string;
    range: TickRange;
    partnerGoalAccount: PlentyGoalAccountFinancialEntity | undefined;
    partnerGoalAccountSnapshots?: FinancialEntitySnapshotFieldsFragment[];
    partnerInitialDepositPlan: number;
    partnerTransactionSchedulePlan: TransactionSchedulePlan;
    partnerAnnualReturn: number;
}) => {
    const currentDate = dt().toDateStr();
    const targetAmount = goal.targetAmount ?? 0;
    const partnerGoalAccountBalance =
        getGoalAccountBalance(partnerGoalAccount) ?? 0;
    const partnerStartingAmount = getGoalForecastStartAmount(
        partnerGoalAccountBalance,
        partnerInitialDepositPlan
    );
    const goalId = goal.id;
    const type = goal.type;

    const myValueNodeBaseParams = {
        goalId,
        type,
        name: 'Your share',
        color: styleguide.colors.blueBright,
    };
    const partnerValueNodeBaseParams = {
        goalId,
        type,
        name: 'Their share',
        color: styleguide.colors.orangeBright,
    };
    const combinedValueNodeBaseParams = {
        goalId,
        type,
        name: 'Total',
        color: configByGoalType[goal.type].color,
    };

    const capBalance = (tick: Tick, balance: number, currentValue: number) => {
        if (!targetAmount || !targetDate) return balance;

        return getCappedBalance({
            balance,
            currentValue,
            targetAmount,
        });
    };

    const getMyForecastValue = (tick: Tick) => {
        return calculateForecastAmount({
            startAmount: initialDepositPlan,
            annualGrowthRate: annualReturn,
            transactionSchedulePlans: [transactionSchedulePlan],
            startDate: currentDate,
            targetDate: getEarliestDate(
                endOfTickDateStr(tick),
                forecastCompletionDate
            ),
        });
    };

    // My entity
    const myVSG = makeValueSeriesGenerator<GoalSnapshot, GoalValueNode>({
        snapshots: [],
        currentDate,
        generatePastValueNode: ({ tick }) => {
            return { ...myValueNodeBaseParams, ...tick, balance: null };
        },
        generateCurrentValueNode: ({ tick }) => {
            const balance = capBalance(
                tick,
                getMyForecastValue(tick),
                initialDepositPlan
            );
            return { ...myValueNodeBaseParams, ...tick, balance };
        },
        generateFutureValueNode: ({ tick }) => {
            const balance = capBalance(
                tick,
                getMyForecastValue(tick),
                initialDepositPlan
            );
            return { ...myValueNodeBaseParams, ...tick, balance };
        },
    });
    const myEntity: GoalsChartEntityParams = {
        id: 'my',
        // N.B. There's no point in memoizing the vsg separately, here,
        // because range will only change as a result of the vsg inputs changing.
        valueSeries: myVSG(range),
        color: styleguide.colors.blueBright,
        strokeWidth: 1.5,
    };

    // Partner entity
    const getPartnerForecastValue = (tick: Tick) => {
        return calculateForecastAmount({
            startAmount: partnerStartingAmount,
            annualGrowthRate: partnerAnnualReturn,
            transactionSchedulePlans: [partnerTransactionSchedulePlan],
            startDate: currentDate,
            targetDate: getEarliestDate(
                endOfTickDateStr(tick),
                forecastCompletionDate
            ),
        });
    };

    const partnerVSG = makeValueSeriesGenerator<GoalSnapshot, GoalValueNode>({
        snapshots: [],
        currentDate,
        generatePastValueNode: ({ tick, snapshot }) => {
            const balance = snapshot?.balance ?? null;
            return { ...partnerValueNodeBaseParams, ...tick, balance };
        },
        generateCurrentValueNode: ({ tick }) => {
            const balance = capBalance(
                tick,
                getPartnerForecastValue(tick),
                partnerGoalAccountBalance
            );
            return { ...partnerValueNodeBaseParams, ...tick, balance };
        },
        generateFutureValueNode: ({ tick }) => {
            const balance = capBalance(
                tick,
                getPartnerForecastValue(tick),
                partnerGoalAccountBalance
            );
            return { ...partnerValueNodeBaseParams, ...tick, balance };
        },
    });
    const partnerEntity: GoalsChartEntityParams = {
        id: 'partner',
        // N.B. There's no point in memoizing the vsg separately, here,
        // because range will only change as a result of the vsg inputs changing.
        valueSeries: partnerVSG(range),
        color: styleguide.colors.orangeBright,
        strokeWidth: 1.5,
    };

    // Combined entity
    const getCombinedForecastValue = (tick: Tick) => {
        const myBalance = getMyForecastValue(tick);
        const partnerBalance = getPartnerForecastValue(tick);
        const balance = myBalance + partnerBalance;
        const currentValue = initialDepositPlan + partnerGoalAccountBalance;

        return capBalance(tick, balance, currentValue);
    };

    const combinedVSG = makeValueSeriesGenerator<GoalSnapshot, GoalValueNode>({
        snapshots: partnerGoalAccountSnapshots ?? [],
        currentDate,
        generatePastValueNode: ({ snapshot, tick }) => {
            const balance = snapshot?.balance ?? null;
            return { ...combinedValueNodeBaseParams, ...tick, balance };
        },
        generateCurrentValueNode: ({ tick }) => {
            const balance = getCombinedForecastValue(tick);
            return { ...combinedValueNodeBaseParams, ...tick, balance };
        },
        generateFutureValueNode: ({ tick }) => {
            const balance = getCombinedForecastValue(tick);

            return { ...combinedValueNodeBaseParams, ...tick, balance };
        },
    });

    const combinedValueSeries = combinedVSG(range);

    const config = configByGoalType[goal.type];

    let callout;
    if (targetDate) {
        const targetTick = makeTick(targetDate, range.unit);
        const targetAmount =
            goal.targetAmount ??
            findValueNode(combinedValueSeries, targetTick).balance;
        if (isNumber(targetAmount)) {
            callout = {
                iconName: config.iconName,
                color: config.color,
                date: makeTick(targetDate, range.unit).date,
                value: targetAmount,
            };
        }
    }
    const combinedEntity: GoalsChartEntityParams = {
        id: 'combined',
        // N.B. There's no point in memoizing the vsg separately, here,
        // because range will only change as a result of the vsg inputs changing.
        valueSeries: combinedVSG(range),
        color: config.color,
        callout,
    };

    return [combinedEntity, partnerEntity, myEntity];
};
